import React from 'react';
import { useTranslation } from 'react-i18next';

import { Skeleton } from '../../../../components';
import { LumioMark } from '../../../creatives/components/LumioMark';
import { Hint } from '../KpiTile/KpiTile.styles';
import { Frame, Lead, Follow, Top, Context, Action, LumioPanel, LeadLine } from './Summary.styles';

export type SummaryProps = {
  /** The headline sentence (already worded and translated). */
  lead: string;
  /** Up to two follow-up sentences. */
  follow: string[];
  loading?: boolean;
  /** What the numbers are narrowed to (the source, the tracking link), above the sentences. */
  context?: React.ReactNode;
  /** A control at the card's top right (the report download). */
  action?: React.ReactNode;
  /** What sits under the sentences on the same card (the figures). */
  children?: React.ReactNode;
  /** How the browsers are counted, as i18n keys read as one muted line between the filters and the sentences. */
  countLine?: string[];
};

/** The tab's overview card: what the numbers are narrowed to and the report download on top, then the one-glance answer
 *  (how many people came, how that compares, what a visit looks like) as a Lumio summary across the card, then the
 *  figures it rests on. */
export const Summary = ({
  lead,
  follow,
  loading = false,
  context,
  action,
  children,
  countLine = [],
}: SummaryProps): React.ReactElement => {
  const { t } = useTranslation();
  return (
    <Frame>
      {context || action ? (
        <Top>
          {context ? <Context>{context}</Context> : <span />}
          {action ? <Action>{action}</Action> : null}
        </Top>
      ) : null}
      {countLine.length > 0 ? <Hint text={countLine.map((key) => t(key)).join(' ')} tag="span" raw /> : null}
      <LumioPanel role="region" aria-label={t('Lumio summary')} aria-live="polite">
        {loading ? (
          <>
            <Skeleton width="60%" height={24} radius={8} />
            <Skeleton width="45%" height={18} radius={6} />
          </>
        ) : (
          <>
            <LeadLine>
              <LumioMark size={20} />
              <Lead text={lead} tag="paragraph" raw />
            </LeadLine>
            {follow.map((line) => (
              <Follow key={line} text={line} tag="paragraph" raw />
            ))}
          </>
        )}
      </LumioPanel>
      {children}
    </Frame>
  );
};

export default Summary;
